import type { HullPaintsExternalAction, HullPaintsVessel } from "../models/hull-paints.models";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";

const HULL_PAINTS_LIBRARY_URL = "https://cargillonline.sharepoint.com/:l:/s/OT_Vessel_Card/JAA9Xh9DMdQRQoBD83PBwTxCAYZIq0kbDRcT5wRvU7HP7BY?nav=MzQ2NzBlZWUtM2I3Ni00YTJmLThjZmYtMjhmOTU5YmQ1ZGM5";

interface HullPaintsExternalActionModalProps {
  action: HullPaintsExternalAction;
  mockEnabled: boolean;
  selectedVessels: HullPaintsVessel[];
  onClose: () => void;
}

const HullPaintsExternalActionModal = ({
  action,
  mockEnabled,
  selectedVessels,
  onClose,
}: HullPaintsExternalActionModalProps) => {
  const isRequest = action === "send-request";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-test="hull-paints-external-action-modal">
      <div className="w-full max-w-xl rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="flex items-start justify-between border-b border-gray-200 p-5 dark:border-gray-700">
          <div className="flex gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 flex-none text-amber-500" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {isRequest ? "Send request to vessel masters" : "Log new paint in library"}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {mockEnabled ? "Mock preview only" : "External PBIP action not migrated"}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-test="hull-paints-close-modal">
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        <div className="space-y-4 p-5 text-sm text-gray-700 dark:text-gray-200">
          {isRequest ? (
            <>
              <p>
                The Power BI report invokes a Power Automate flow with vessel email, name, and IMO. OpsLink does not send that external request from this page.
              </p>
              <p className="font-medium">{selectedVessels.length} vessel{selectedVessels.length === 1 ? "" : "s"} in the request context.</p>
              <div className="max-h-44 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
                {selectedVessels.length === 0 ? (
                  <p className="p-3 text-gray-500">No vessels match the current filters.</p>
                ) : selectedVessels.map((vessel) => (
                  <p key={vessel.imoNumber} className="border-b border-gray-100 px-3 py-2 last:border-0 dark:border-gray-700">
                    {vessel.vesselName} · {vessel.imoNumber} · {vessel.email ?? "No email in source"}
                  </p>
                ))}
              </div>
            </>
          ) : (
            <>
              <p>
                The Power BI button opens a SharePoint list. That external log destination is outside the Snowflake migration boundary, so this replica does not create paint records.
              </p>
              <a
                href={HULL_PAINTS_LIBRARY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex rounded-lg border border-green px-3 py-2 font-semibold text-green hover:bg-primary-10"
                data-test="hull-paints-open-sharepoint-library"
              >
                Open SharePoint paint library
              </a>
            </>
          )}
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 dark:border-gray-700">
          <button type="button" onClick={onClose} className="rounded-lg bg-green px-4 py-2 text-sm font-semibold text-white" data-test="hull-paints-acknowledge-external-action">
            Understood
          </button>
        </div>
      </div>
    </div>
  );
};

export default HullPaintsExternalActionModal;
